import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trash2, Search, ChevronDown, Send, Info, Save } from 'lucide-react';
import { DiscrepancyService } from '../../services/discrepancyService';
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button, ConfirmDialog } from '../ui';
import FileUpload from '../FileUpload/FileUpload';
import ContactDetailsModal from '../ContactDetailsModal';
import { formatJobRevNumber } from '../../utils/projectFormatting';

// Format project display for dropdown
const formatProjectDisplay = (project) => {
  if (!project) return '';
  let display = '';
  const jobRevNumber = formatJobRevNumber(project.job_number, project.rev);
  if (jobRevNumber) {
    display = jobRevNumber + ' - ';
  }
  display += project.name || 'Unnamed Project';
  return display;
};

const DiscrepancyModal = ({
  discrepancy,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isCreating = false,
  zIndex = null,
  context = null
}) => {
  const [formData, setFormData] = useState({
    discrepancyType: '',
    discrepancyStatus: 'Draft',
    projectId: null,
    loomLink: '',
    markupAttachment: [],
    additionalInformation: '',
    createdBy: '',
  });

  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [projectDetails, setProjectDetails] = useState(null);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [createdByName, setCreatedByName] = useState('');
  const [sentByName, setSentByName] = useState('');

  const projectSearchRef = useRef(null);
  const dropdownRef = useRef(null);
  const autoSaveTimerRef = useRef(null);

  // Discrepancy type options
  // When opened from Export & Send modal, only show specific types
  const typeOptions = (() => {
    const allTypes = DiscrepancyService.getDiscrepancyTypes();

    // Filter types when context is "export-send"
    if (context === "export-send") {
      const allowedTypes = [
        "Grading Clarification",
        "Utility Discrepancy",
        "Profile Discrepancy"
      ];
      return allTypes
        .filter(type => allowedTypes.includes(type))
        .map(type => ({
          value: type,
          label: type,
        }));
    }

    // Show all types for other contexts (discrepancy board, etc.)
    return allTypes.map(type => ({
      value: type,
      label: type,
    }));
  })();

  // Status options
  const statusOptions = DiscrepancyService.getDiscrepancyStatuses().map(status => ({
    value: status,
    label: status,
  }));

  // Initialize form data
  useEffect(() => {
    if (isCreating) {
      // When creating, check if a projectId was provided (e.g., from Export & Send)
      const projectIdToSet = discrepancy?.projectId || discrepancy?.project_id || discrepancy?.project?.id || null;

      setFormData({
        discrepancyType: '',
        discrepancyStatus: 'Draft',
        projectId: projectIdToSet,
        loomLink: '',
        markupAttachment: [],
        additionalInformation: '',
        createdBy: '',
      });

      // If projectId was provided, load its details
      if (projectIdToSet) {
        loadProjectDetails(projectIdToSet);
      } else {
        setProjectDetails(null);
        setProjectSearchTerm('');
      }
      setShowProjectDropdown(false);
    } else if (discrepancy) {
      const projectIdToSet = discrepancy.projectId || discrepancy.project_id || discrepancy.project?.id || null;

      setFormData({
        discrepancyType: discrepancy.discrepancyType || discrepancy.type || '',
        discrepancyStatus: discrepancy.discrepancyStatus || discrepancy.status || 'Draft',
        projectId: projectIdToSet,
        loomLink: discrepancy.loomLink || discrepancy.loom_link || '',
        markupAttachment: (() => {
          const attachment = discrepancy.markupAttachment || discrepancy.markup_attachment;
          if (Array.isArray(attachment)) {
            return attachment;
          } else if (typeof attachment === 'string') {
            try {
              const parsed = JSON.parse(attachment);
              return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              return [];
            }
          }
          return [];
        })(),
        additionalInformation: discrepancy.additionalInformation || discrepancy.additional_information || '',
        createdBy: discrepancy.createdByName || discrepancy.created_by_name || discrepancy.createdBy || discrepancy.created_by || '',
      });

      // Load project details
      if (projectIdToSet) {
        loadProjectDetails(projectIdToSet);
      }
    }
  }, [discrepancy, isCreating]);

  // Load projects for dropdown
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data, error } = await supabase
          .from('projects')
          .select(`
            id,
            name,
            job_number,
            rev,
            client_id,
            contact_id,
            clients (
              id,
              name
            ),
            contacts (
              id,
              name,
              email
            )
          `)
          .order('job_number', { ascending: false });

        if (error) throw error;
        setProjects(data || []);
        setFilteredProjects(data || []);
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    };

    if (isOpen && isCreating) {
      loadProjects();
    }
  }, [isOpen, isCreating]);

  // Load project details
  const loadProjectDetails = async (projectId) => {
    try {
      const projectData = await DiscrepancyService.getProjectWithContacts(projectId);
      setProjectDetails(projectData);
      // Also set the search term to show the project name
      if (projectData) {
        const displayText = formatProjectDisplay(projectData);
        setProjectSearchTerm(displayText);
      }
    } catch (err) {
      console.error('Error loading project details:', err);
    }
  };

  // Filter projects based on search
  useEffect(() => {
    if (!projectSearchTerm) {
      setFilteredProjects(projects);
      return;
    }

    const searchLower = projectSearchTerm.toLowerCase().trim();
    const filtered = projects.filter((project) => {
      const projectName = (project.name || '').toLowerCase();
      const jobNumber = (project.job_number || '').toLowerCase();
      const formattedJobRev = formatJobRevNumber(project.job_number, project.rev).toLowerCase();
      const clientName = (project.clients?.name || '').toLowerCase();
      const contactName = (project.contacts?.name || '').toLowerCase();
      const contactEmail = (project.contacts?.email || '').toLowerCase();

      return (
        projectName.includes(searchLower) ||
        jobNumber.includes(searchLower) ||
        formattedJobRev.includes(searchLower) ||
        clientName.includes(searchLower) ||
        contactName.includes(searchLower) ||
        contactEmail.includes(searchLower)
      );
    });

    setFilteredProjects(filtered);
  }, [projectSearchTerm, projects]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
    };

    if (showProjectDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProjectDropdown]);

  // Look up creator name from email
  useEffect(() => {
    const lookupCreatorName = async () => {
      if (!formData.createdBy) return;

      // Check if createdBy is an email (contains @)
      if (formData.createdBy.includes('@')) {
        try {
          const { supabase } = await import('../../lib/supabase');
          const { data, error } = await supabase
            .from('employees')
            .select('name')
            .eq('email', formData.createdBy)
            .single();

          if (!error && data?.name) {
            setCreatedByName(data.name);
          } else {
            // If not found in employees, use email
            setCreatedByName(formData.createdBy);
          }
        } catch (err) {
          console.error('Error looking up creator name:', err);
          setCreatedByName(formData.createdBy);
        }
      } else {
        // Already a name, use as is
        setCreatedByName(formData.createdBy);
      }
    };

    lookupCreatorName();
  }, [formData.createdBy]);

  // Look up sent by name from email
  useEffect(() => {
    const lookupSentByName = async () => {
      if (!discrepancy) return;

      const lastModifiedBy = discrepancy.lastModifiedBy || discrepancy.last_modified_by;
      if (!lastModifiedBy) return;

      // Check if lastModifiedBy is an email (contains @)
      if (lastModifiedBy.includes('@')) {
        try {
          const { supabase } = await import('../../lib/supabase');
          const { data, error } = await supabase
            .from('employees')
            .select('name')
            .eq('email', lastModifiedBy)
            .single();

          if (!error && data?.name) {
            setSentByName(data.name);
          } else {
            // If not found in employees, use email
            setSentByName(lastModifiedBy);
          }
        } catch (err) {
          console.error('Error looking up sent by name:', err);
          setSentByName(lastModifiedBy);
        }
      } else {
        // Already a name, use as is
        setSentByName(lastModifiedBy);
      }
    };

    lookupSentByName();
  }, [discrepancy]);

  // Handle project selection
  const handleProjectSelect = async (projectId) => {
    const selectedProject = projects.find((p) => p.id === projectId);
    if (selectedProject) {
      try {
        const projectData = await DiscrepancyService.getProjectWithContacts(projectId);
        setProjectDetails(projectData);
        setFormData((prev) => ({ ...prev, projectId }));
        setProjectSearchTerm(formatProjectDisplay(selectedProject));
        setShowProjectDropdown(false);

        // Clear error
        if (errors.projectId) {
          setErrors((prev) => ({ ...prev, projectId: undefined }));
        }
      } catch (err) {
        console.error('Error loading project details:', err);
        setFormData((prev) => ({ ...prev, projectId }));
      }
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle file changes
  const handleFilesChange = (newFiles) => {
    setFormData((prev) => ({ ...prev, markupAttachment: newFiles }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.discrepancyType) {
      newErrors.discrepancyType = 'Discrepancy type is required';
    }

    if (!formData.projectId) {
      newErrors.projectId = 'Project selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSave(formData, true); // Pass true to close modal after save
      setErrors({});
      // Close modal after successful save when creating
      if (isCreating) {
        onClose();
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to save discrepancy' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-save function (for export-send context)
  const performAutoSave = useCallback(async () => {
    // Only auto-save in export-send context and when editing (not creating)
    if (context !== 'export-send' || isCreating) return;
    if (!discrepancy?.id) return;

    try {
      await onSave(formData);
    } catch (err) {
      console.error('Auto-save failed:', err);
      // Don't show errors on auto-save failures to avoid disrupting user
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, isCreating, discrepancy?.id, formData]);

  // Debounced auto-save effect
  useEffect(() => {
    // Only auto-save in export-send context when editing
    if (context !== 'export-send' || isCreating) return;
    if (!discrepancy?.id) return;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for 2 seconds
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
    }, 2000);

    // Cleanup timer on unmount
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, context, isCreating, discrepancy?.id]);

  // Handle send to review (for Draft status)
  const handleSendToReview = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const updatedFormData = {
        ...formData,
        discrepancyStatus: 'Waiting to Review',
      };
      await onSave(updatedFormData);
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to send to review' });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = () => {
    console.log("🗑️ Delete button clicked", { discrepancy: discrepancy?.id, isCreating });
    if (!discrepancy || isCreating) {
      console.log("⚠️ Cannot delete - no discrepancy or is creating mode");
      return;
    }
    console.log("✅ Showing delete confirmation dialog");
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    console.log("🗑️ Delete confirmed", { discrepancyId: discrepancy?.id });
    if (!discrepancy?.id) {
      console.log("⚠️ Cannot delete - no discrepancy ID");
      return;
    }

    setLoading(true);
    try {
      console.log("🗑️ Calling onDelete callback...");
      await onDelete(discrepancy.id);
      console.log("✅ Delete callback completed successfully");
      setShowDeleteConfirm(false);
      console.log("✅ Closing modal...");
      onClose();
    } catch (err) {
      console.error("❌ Delete failed:", err);
      setErrors({ submit: err.message || 'Failed to delete discrepancy' });
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'Startup': '#6b7280',
      'Data Prep': '#3b82f6',
      'Ready to Model': '#8b5cf6',
      'Modeling': '#f59e0b',
      'Double Check': '#ec4899',
      'Corrections': '#f97316',
      'Review': '#14b8a6',
      'Ready to Export': '#10b981',
      'Completed': '#22c55e',
      'Utilities': '#64748b'
    };
    return statusColors[status] || '#6b7280';
  };

  return (
    <>
      <Modal trackAs="DiscrepancyModal" isOpen={isOpen} onClose={onClose} size="lg" zIndex={zIndex}>
        {/* Custom Header with Icon */}
        <div
          style={{
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--gray-700)',
            background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {!isCreating && projectDetails ? (
              <>
                {/* Match FullSubitemModal format: Job number and project name on same line */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
                  <span
                    style={{
                      fontSize: 'var(--text-2xl)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-400)',
                      flexShrink: 0,
                    }}
                  >
                    {formatJobRevNumber(projectDetails.job_number, projectDetails.rev)}
                  </span>
                  <h1
                    style={{
                      margin: 0,
                      fontSize: 'var(--text-2xl)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-50)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {projectDetails.name}
                  </h1>
                </div>
                <p
                  style={{
                    margin: 0,
                    marginTop: 'var(--space-1)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-400)',
                  }}
                >
                  {formData.discrepancyType || 'Discrepancy'} - {formData.discrepancyStatus}
                </p>
              </>
            ) : (
              <>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-50)',
                  }}
                >
                  New Discrepancy
                </h1>
                <p
                  style={{
                    margin: 0,
                    marginTop: 'var(--space-1)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-400)',
                  }}
                >
                  Create a new discrepancy or clarification
                </p>
              </>
            )}
          </div>
        </div>

        <ModalBody>
          {/* Error message */}
          {errors.submit && (
            <div
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Project Information Header (show when not creating) */}
          {!isCreating && projectDetails && (
            <div
              style={{
                padding: 'var(--space-4)',
                marginBottom: 'var(--space-6)',
                backgroundColor: 'var(--dark-elevated)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-1)' }}>
                    Project
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)', fontWeight: 'var(--font-medium)' }}>
                    {formatJobRevNumber(projectDetails.job_number, projectDetails.rev)} - {projectDetails.name}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-1)' }}>
                    Client
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)', fontWeight: 'var(--font-medium)' }}>
                    {projectDetails.clients?.name || 'Unknown'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-1)' }}>
                    Primary Contact
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)', fontWeight: 'var(--font-medium)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    {projectDetails.contacts && (
                      <button
                        onClick={() => setShowContactModal(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--gray-400)',
                          transition: 'color var(--transition-fast)',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cta-blue)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--gray-400)'}
                        title="View contact details"
                      >
                        <Info size={16} />
                      </button>
                    )}
                    <span>
                      {projectDetails.contacts?.name || 'No Contact'}
                      {projectDetails.contacts?.email && (
                        <span style={{ color: 'var(--gray-400)', marginLeft: 'var(--space-2)' }}>
                          ({projectDetails.contacts.email})
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 'var(--space-1)' }}>
                    Modeling Status
                  </div>
                  <div
                    style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      color: 'white',
                      backgroundColor: getStatusColor(projectDetails.modeling_status),
                    }}
                  >
                    {projectDetails.modeling_status || 'No Status'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Two column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column: Discrepancy Details */}
            <div style={{ minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Discrepancy Details
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Project Selection - Only show when creating */}
                {isCreating && (
                  <div ref={dropdownRef}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Project <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <div
                        style={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Search
                          size={16}
                          style={{
                            position: 'absolute',
                            left: 'var(--space-3)',
                            color: 'var(--gray-400)',
                            pointerEvents: 'none',
                          }}
                        />
                        <input
                          ref={projectSearchRef}
                          type="text"
                          value={projectSearchTerm}
                          onChange={(e) => {
                            setProjectSearchTerm(e.target.value);
                            setShowProjectDropdown(true);
                          }}
                          onFocus={() => setShowProjectDropdown(true)}
                          placeholder="Search by project name, job number, or client..."
                          style={{
                            width: '100%',
                            padding: 'var(--space-2) var(--space-3) var(--space-2) 36px',
                            backgroundColor: 'var(--dark-elevated)',
                            border: `1px solid ${errors.projectId ? 'var(--danger)' : 'var(--gray-600)'}`,
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--gray-100)',
                            fontSize: 'var(--text-sm)',
                            outline: 'none',
                          }}
                        />
                        <ChevronDown
                          size={16}
                          style={{
                            position: 'absolute',
                            right: 'var(--space-3)',
                            color: 'var(--gray-400)',
                            pointerEvents: 'none',
                          }}
                        />
                      </div>

                      {/* Dropdown */}
                      {showProjectDropdown && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            maxHeight: '300px',
                            overflowY: 'auto',
                            backgroundColor: 'var(--dark-elevated)',
                            border: '1px solid var(--gray-600)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: 'var(--shadow-lg)',
                            zIndex: 1000,
                          }}
                        >
                          {filteredProjects.length === 0 ? (
                            <div
                              style={{
                                padding: 'var(--space-4)',
                                textAlign: 'center',
                                color: 'var(--gray-400)',
                                fontSize: 'var(--text-sm)',
                              }}
                            >
                              {projectSearchTerm ? 'No projects found' : 'No projects available'}
                            </div>
                          ) : (
                            filteredProjects.map((project) => (
                              <div
                                key={project.id}
                                onClick={() => handleProjectSelect(project.id)}
                                style={{
                                  padding: 'var(--space-3)',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid var(--gray-700)',
                                  backgroundColor: formData.projectId === project.id ? 'var(--gray-700)' : 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                  if (formData.projectId !== project.id) {
                                    e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (formData.projectId !== project.id) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)', fontWeight: 'var(--font-medium)' }}>
                                  <span style={{ color: 'var(--cta-blue)' }}>
                                    {formatJobRevNumber(project.job_number, project.rev)}
                                  </span>
                                  {' '}{project.name || 'Unnamed Project'}
                                </div>
                                {project.clients?.name && (
                                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', marginTop: 'var(--space-1)' }}>
                                    {project.clients.name}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {errors.projectId && (
                      <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>
                        {errors.projectId}
                      </div>
                    )}
                  </div>
                )}

                {/* Type */}
                <Dropdown
                  label="Type"
                  options={typeOptions}
                  value={formData.discrepancyType}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, discrepancyType: value }));
                    if (errors.discrepancyType) {
                      setErrors((prev) => ({ ...prev, discrepancyType: undefined }));
                    }
                  }}
                  placeholder="Select type"
                  error={errors.discrepancyType}
                  required
                />

                {/* Status */}
                <Dropdown
                  label="Status"
                  options={statusOptions}
                  value={formData.discrepancyStatus}
                  onChange={(value) => setFormData((prev) => ({ ...prev, discrepancyStatus: value }))}
                  disabled={false}
                />

                {/* Loom Link */}
                <Input
                  label="Loom Link"
                  name="loomLink"
                  type="url"
                  value={formData.loomLink}
                  onChange={handleChange}
                  placeholder="https://loom.com/..."
                />

                {/* Record Information - Only show when viewing existing discrepancy */}
                {!isCreating && discrepancy && (
                  <div
                    style={{
                      marginTop: 'var(--space-4)',
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        marginBottom: 'var(--space-3)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Record Information
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                      {/* Created By and Date */}
                      <div>
                        <div
                          style={{
                            fontSize: 'var(--text-xs)',
                            color: 'var(--gray-500)',
                            textTransform: 'uppercase',
                            marginBottom: 'var(--space-1)',
                          }}
                        >
                          Created By
                        </div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)' }}>
                          {createdByName || formData.createdBy || 'Unknown'}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 'var(--text-xs)',
                            color: 'var(--gray-500)',
                            textTransform: 'uppercase',
                            marginBottom: 'var(--space-1)',
                          }}
                        >
                          Created
                        </div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)' }}>
                          {formatDate(discrepancy.createdAt || discrepancy.created_at)}
                        </div>
                      </div>

                      {/* Sent By and Date - Only show if sent_date exists */}
                      {(discrepancy.sentDate || discrepancy.sent_date) && (
                        <>
                          <div>
                            <div
                              style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--gray-500)',
                                textTransform: 'uppercase',
                                marginBottom: 'var(--space-1)',
                              }}
                            >
                              Sent By
                            </div>
                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)' }}>
                              {sentByName || 'Unknown'}
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--gray-500)',
                                textTransform: 'uppercase',
                                marginBottom: 'var(--space-1)',
                              }}
                            >
                              Sent Date
                            </div>
                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)' }}>
                              {formatDate(discrepancy.sentDate || discrepancy.sent_date)}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Additional Information */}
            <div style={{ minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Additional Information
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Additional Information */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--gray-300)',
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    name="additionalInformation"
                    value={formData.additionalInformation}
                    onChange={handleChange}
                    placeholder="Describe the discrepancy or provide additional context..."
                    rows={6}
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-100)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      outline: 'none',
                    }}
                  />
                </div>

                {/* File Attachments */}
                <div>
                  {/* Show existing files prominently when viewing */}
                  {!isCreating && formData.markupAttachment.length > 0 && (
                    <>
                      <div
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                          color: 'var(--gray-300)',
                          marginBottom: 'var(--space-3)',
                        }}
                      >
                        Attached Files ({formData.markupAttachment.length})
                      </div>
                      <div
                        style={{
                          marginBottom: 'var(--space-4)',
                          paddingBottom: 'var(--space-3)',
                          borderBottom: '1px solid var(--gray-700)',
                          maxHeight: '300px',
                          overflowY: 'auto',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                          {formData.markupAttachment.map((file, index) => (
                            <div
                              key={file.id || index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: 'var(--space-2) 0',
                                borderBottom: index < formData.markupAttachment.length - 1 ? '1px solid var(--gray-800)' : 'none',
                                transition: 'background-color 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                                e.currentTarget.style.paddingLeft = 'var(--space-2)';
                                e.currentTarget.style.paddingRight = 'var(--space-2)';
                                e.currentTarget.style.marginLeft = 'calc(-1 * var(--space-2))';
                                e.currentTarget.style.marginRight = 'calc(-1 * var(--space-2))';
                                e.currentTarget.style.borderRadius = 'var(--radius-md)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.paddingLeft = '0';
                                e.currentTarget.style.paddingRight = '0';
                                e.currentTarget.style.marginLeft = '0';
                                e.currentTarget.style.marginRight = '0';
                                e.currentTarget.style.borderRadius = '0';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {file.filename || file.original_filename || file.name}
                                </div>
                              </div>
                              {file.url && (
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    fontSize: 'var(--text-sm)',
                                    color: 'var(--cta-blue)',
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'var(--font-medium)',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = 'underline';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = 'none';
                                  }}
                                >
                                  View
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Upload area - shown as "Add more files" when viewing with existing files */}
                  {!isCreating && formData.markupAttachment.length > 0 && (
                    <div
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                        marginBottom: 'var(--space-3)',
                      }}
                    >
                      Add More Files
                    </div>
                  )}

                  {isCreating && (
                    <div
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                        marginBottom: 'var(--space-3)',
                      }}
                    >
                      File Attachments
                    </div>
                  )}

                  <FileUpload
                    files={formData.markupAttachment}
                    onFilesChange={handleFilesChange}
                    projectId={formData.projectId}
                    clientId={projectDetails?.client_id}
                    disabled={loading}
                    maxFiles={10}
                    showFileList={isCreating || formData.markupAttachment.length === 0}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            {/* Left side - Delete button */}
            <div>
              {!isCreating && (
                <Button variant="danger" onClick={handleDelete} disabled={loading}>
                  <Trash2 size={14} />
                  Delete
                </Button>
              )}
            </div>

            {/* Right side - Action buttons */}
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              {/* Save button - always visible */}
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={loading}
                style={{
                  backgroundColor: 'var(--cta-orange)',
                  color: 'white',
                  border: 'none'
                }}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save'}
              </Button>

              {/* Send to Review - only for Draft status outside export-send context when editing */}
              {!isCreating && formData.discrepancyStatus === 'Draft' && context !== 'export-send' && (
                <Button variant="secondary" onClick={handleSendToReview} disabled={loading}>
                  <Send size={16} />
                  {loading ? 'Sending...' : 'Send to Review'}
                </Button>
              )}
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Discrepancy"
        message="Are you sure you want to delete this discrepancy? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={loading}
        zIndex={zIndex ? zIndex + 1 : 10001}
      />

      {/* Contact Details Modal */}
      {showContactModal && projectDetails?.contacts && (
        <ContactDetailsModal
          contact={projectDetails.contacts}
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          onSave={async (contactId, updates) => {
            setShowContactModal(false);
          }}
          zIndex={10001}
        />
      )}
    </>
  );
};

export default DiscrepancyModal;
